// Per-frame HUD values drawn on canvases instead of DOM text and inline styles (RALLY_GOAL.md §14:
// no GC pause over 2 ms). Every DOM text or style write allocates in Blink's garbage-collected heap
// (a new text node and layout fragments for `textContent`, parsed CSS values and a new computed style
// for `style.transform`): ~2–4 KB each, ~0.5 MB/s with the HUD changing every frame, enough to force a
// full (V8 + Blink) mark-compact every ~30 s. A canvas that only blits glyphs from a pre-rendered atlas
// and fills rectangles allocates nothing per update, and its element never changes size, so there is
// no relayout either.

interface Glyph {
  /** Left edge in the atlas (device px). */
  x: number;
  /** Advance (CSS px). */
  adv: number;
}

function cssVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * A run of text in the host element's font and colour, drawn from a glyph atlas onto a canvas of
 * fixed width (room for `maxLen` characters), aligned left, right or centre inside it. Digits are
 * tabular. The canvas sits on the host's text baseline (a zero-width strut keeps the line box), so
 * the host lays out exactly as it did with text.
 */
export class GlyphText {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly atlas: HTMLCanvasElement;
  private readonly glyphs = new Map<string, Glyph>();
  private dpr = 0;
  /** devicePixelRatio the atlas was built for (raw, to notice changes). */
  private builtDpr = -1;
  private cellH = 0;
  /** Canvas size (CSS px) and the baseline/padding inside it. */
  private w = 0;
  private h = 0;
  private baseline = 0;
  private pad = 0;
  private last = '';

  constructor(
    private readonly host: HTMLElement,
    private readonly chars: string,
    private readonly maxLen: number,
    private readonly align: 'left' | 'right' | 'center',
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'glyph-text';
    this.ctx = this.canvas.getContext('2d')!;
    this.atlas = document.createElement('canvas');
    host.textContent = '​';
    host.appendChild(this.canvas);
    document.fonts?.addEventListener?.('loadingdone', () => this.rebuild());
  }

  /** Re-renders the atlas (after a font load, a DPR change or a style change of the host). */
  rebuild(): void {
    this.builtDpr = -1;
    const t = this.last;
    this.last = '\u0000';
    this.set(t);
  }

  private build(): void {
    const cs = getComputedStyle(this.host);
    const size = parseFloat(cs.fontSize) || 16;
    const font = `${cs.fontStyle} ${cs.fontWeight} ${size}px ${cs.fontFamily}`;
    this.builtDpr = devicePixelRatio;
    const dpr = (this.dpr = Math.max(1, devicePixelRatio || 1));
    const m = this.ctx;
    m.font = font;
    // Tabular digits: every digit advances by the widest one.
    let digit = 0;
    for (const c of '0123456789') digit = Math.max(digit, m.measureText(c).width);
    const zero = m.measureText('0');
    const asc = zero.fontBoundingBoxAscent ?? size * 0.92;
    const desc = zero.fontBoundingBoxDescent ?? size * 0.24;
    const lh = cs.lineHeight === 'normal' ? asc + desc : parseFloat(cs.lineHeight) || asc + desc;
    // Room for the italic overhang on both sides.
    this.pad = Math.ceil(size * 0.18);
    this.cellH = Math.ceil(lh);
    this.baseline = (lh - (asc + desc)) / 2 + asc;
    this.glyphs.clear();
    let x = 0;
    const adv: number[] = [];
    for (const c of this.chars) {
      const a = c >= '0' && c <= '9' ? digit : m.measureText(c).width;
      adv.push(a);
      this.glyphs.set(c, { x: Math.round(x * dpr), adv: a });
      x += a + this.pad * 2;
    }
    this.atlas.width = Math.ceil(x * dpr) + 2;
    this.atlas.height = Math.ceil(this.cellH * dpr);
    const a = this.atlas.getContext('2d')!;
    a.clearRect(0, 0, this.atlas.width, this.atlas.height);
    a.font = font;
    a.fillStyle = cs.color;
    a.textBaseline = 'alphabetic';
    a.textAlign = 'center';
    a.setTransform(dpr, 0, 0, dpr, 0, 0);
    let i = 0;
    for (const c of this.chars) {
      const g = this.glyphs.get(c)!;
      a.fillText(c, g.x / dpr + this.pad + adv[i] / 2, this.baseline);
      i++;
    }
    this.w = Math.ceil(digit * this.maxLen) + this.pad * 2;
    this.h = this.cellH;
    this.canvas.width = Math.ceil(this.w * dpr);
    this.canvas.height = Math.ceil(this.h * dpr);
    const st = this.canvas.style;
    st.width = `${this.w}px`;
    st.height = `${this.h}px`;
    // Canvas baseline on the line's baseline; the padding overhangs the neighbours.
    st.verticalAlign = `${-(this.h - this.baseline)}px`;
    st.margin = `0 ${-this.pad}px`;
  }

  /** Draws `text` (characters outside the set are skipped). No-op when unchanged. */
  set(text: string): void {
    const fresh = this.builtDpr !== devicePixelRatio;
    if (text === this.last && !fresh) return;
    this.last = text;
    // Style reads allocate too: only on first use, after a font load or a DPR change.
    if (fresh) this.build();
    const c = this.ctx;
    const dpr = this.dpr;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, this.canvas.width, this.canvas.height);
    let width = 0;
    for (let i = 0; i < text.length; i++) width += this.glyphs.get(text[i])?.adv ?? 0;
    const inner = this.w - this.pad * 2;
    let x = this.align === 'left' ? 0 : this.align === 'right' ? inner - width : (inner - width) / 2;
    const gw = this.pad * 2;
    const hh = this.canvas.height;
    for (let i = 0; i < text.length; i++) {
      const g = this.glyphs.get(text[i]);
      if (!g) continue;
      const sw = Math.round((g.adv + gw) * dpr);
      c.drawImage(this.atlas, g.x, 0, sw, hh, Math.round(x * dpr), 0, sw, hh);
      x += g.adv;
    }
  }
}

/** A canvas filling its host's content box, redrawn by the owner with rectangles. */
export class RectCanvas {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  /** Size in CSS px. */
  w = 0;
  h = 0;
  private dpr = 0;

  constructor(
    private readonly host: HTMLElement,
    private readonly extend = { x: 0, y: 0 },
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'rect-canvas';
    this.ctx = this.canvas.getContext('2d')!;
    host.appendChild(this.canvas);
  }

  /** Sizes the canvas to the host (+ the overhang); true if it changed (the owner should redraw). */
  fit(): boolean {
    const w = this.host.clientWidth + this.extend.x * 2;
    const h = this.host.clientHeight + this.extend.y * 2;
    const dpr = Math.max(1, devicePixelRatio || 1);
    if (w === this.w && h === this.h && dpr === this.dpr) return false;
    this.w = w;
    this.h = h;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.ceil(w * dpr));
    this.canvas.height = Math.max(1, Math.ceil(h * dpr));
    const st = this.canvas.style;
    st.width = `${w}px`;
    st.height = `${h}px`;
    st.left = `${-this.extend.x}px`;
    st.top = `${-this.extend.y}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return true;
  }

  /** Fits only when the DPR changed (a cheap check per frame; the host's size is fixed by CSS). */
  stale(): boolean {
    return this.dpr !== Math.max(1, devicePixelRatio || 1) || this.w === 0;
  }
}

export { cssVar };
